import { produce, type Draft } from 'immer';
import { DOWNLOAD_TYPE, PINNED_STORAGE_KEY } from '../../constants';
import {
  BOOKMARKS_SET,
  REMOVE_BOOKMARK_ITEM,
  REMOVE_BOOKMARK_GROUP,
  SET_ATTRIBUTES,
  SET_LOGIN_STATUS,
  UPDATE_TOAST,
} from '../actions';
import { IContextState, initialBookmarkState } from '../state';
import { setBookmarkCount, sortBookmarksByPinOrder } from '../../utils/bookmark';
import type { IAttribute, IItem } from '../../types';

export const BOOKMARK_SELECT_ALL = 'df/BOOKMARK_SELECT_ALL';
export const BOOKMARK_ADD_TO_ZIP = 'df/BOOKMARK_ADD_TO_ZIP';
export const BOOKMARK_REMOVE_FROM_ZIP = 'df/BOOKMARK_REMOVE_FROM_ZIP';
export const BOOKMARK_START_DOWNLOAD = 'df/BOOKMARK_START_DOWNLOAD';
export const BOOKMARK_REMOVE_GROUP_FROM_ZIP = 'df/BOOKMARK_REMOVE_GROUP_FROM_ZIP';
export const SET_BOOKMARK_EXPANDED = 'df/SET_BOOKMARK_EXPANDED';
export const BOOKMARK_DATA = 'df/BOOKMARK_DATA';

const getZipSize = (selectedBookmarks?: Array<number>, bookmarkItems?: Array<IItem>) => {
  if (!selectedBookmarks || !bookmarkItems) return 0;

  return selectedBookmarks.reduce((fileSize: number, pimId: number) => {
    const dataItem = bookmarkItems.find((el) => el.pimId === pimId);
    return fileSize + (dataItem?.dfAttributeDfFileSize || 0);
  }, 0);
};

export const bookmarkReducer = produce(
  (
    draft: Draft<IContextState>,
    action: {
      type: string;
      item?: any;
      items?: Array<any>;
      data?: any;
      expanded?: number | string;
      showToast?: boolean;
      message?: string;
      attributes?: Array<IAttribute>;
      group?: any;
      loggedIn?: boolean;
      bookmarks?: Array<{ id: number; group: number }>;
    },
  ) => {
    switch (action.type) {
      case BOOKMARKS_SET: {
        draft.bookmarks = action.bookmarks || [];
        setBookmarkCount(action.bookmarks?.length || 0);
        break;
      }
      case BOOKMARK_DATA: {
        draft.bookmarkData = sortBookmarksByPinOrder(draft.bookmarks, [
          ...action.data.bookmarks,
          ...(draft.bookmarkData || []),
        ]);
        draft.bookmarkItems = draft.bookmarkData?.map((item) => item.items).flat();
        break;
      }
      case SET_BOOKMARK_EXPANDED: {
        draft.expanded = action.expanded;
        break;
      }
      case BOOKMARK_SELECT_ALL: {
        const allItems = action.items?.filter((itemId: number) => {
          const dataEntry = draft.bookmarkItems?.find((itemData) => itemData.pimId === itemId);
          if (dataEntry?.type) {
            return dataEntry.type !== DOWNLOAD_TYPE.LOGIN;
          }
          return false;
        });
        draft.selectedBookmarks = allItems?.map((item: number) => item) || [];
        draft.zipSize = getZipSize(draft.selectedBookmarks, draft.bookmarkItems);
        draft.zipItems = draft.selectedBookmarks.length;
        break;
      }
      case BOOKMARK_ADD_TO_ZIP: {
        if (!draft.selectedBookmarks?.includes(action.item)) {
          draft.selectedBookmarks = draft.selectedBookmarks?.concat(action.item);
          draft.zipSize = getZipSize(draft.selectedBookmarks, draft.bookmarkItems);
          draft.zipItems = draft.selectedBookmarks?.length || 0;
        }
        break;
      }
      case BOOKMARK_REMOVE_FROM_ZIP: {
        if (draft.selectedBookmarks?.includes(action.item)) {
          draft.selectedBookmarks = draft.selectedBookmarks.filter((item) => item !== action.item);
          draft.zipSize = getZipSize(draft.selectedBookmarks, draft.bookmarkItems);
          draft.zipItems = draft.selectedBookmarks.length;
        }
        break;
      }
      case BOOKMARK_REMOVE_GROUP_FROM_ZIP: {
        if (draft.selectedBookmarks?.includes(action.item)) {
          draft.bookmarkData = draft.bookmarkData?.filter((item) => item.pimId !== action.item);
          draft.bookmarkItems = draft.bookmarkData?.map((item) => item.items).flat();
          draft.selectedBookmarks = draft.selectedBookmarks.filter((item) => item !== action.item);
          draft.zipSize = getZipSize(draft.selectedBookmarks, draft.bookmarkItems);
          draft.zipItems = draft.selectedBookmarks.length;
        }
        break;
      }
      case BOOKMARK_START_DOWNLOAD: {
        console.log('Initialize download for items', draft.selectedBookmarks);
        break;
      }
      case REMOVE_BOOKMARK_GROUP: {
        draft.bookmarks = draft.bookmarks.filter((item) => item.group !== action.group);
        draft.bookmarkData = draft.bookmarkData?.filter((item) => item.pimId !== action.group);
        setBookmarkCount(draft.bookmarks.length);
        window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(draft.bookmarks));
        break;
      }
      case REMOVE_BOOKMARK_ITEM: {
        draft.bookmarks = draft.bookmarks.filter((item) => item.id !== action.item);
        draft.bookmarkData = draft.bookmarkData
          ?.map((data) => ({
            ...data,
            items: data.items.filter((item) => item.pimId !== action.item),
          }))
          .filter((data) => data.items.length > 0);

        setBookmarkCount(draft.bookmarks.length);
        window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(draft.bookmarks));
        break;
      }
      case SET_ATTRIBUTES: {
        draft.attributes = action.attributes || [];
        break;
      }
      case UPDATE_TOAST: {
        draft.showToast = !!action.showToast;
        draft.message = action.message || '';
        break;
      }
      case SET_LOGIN_STATUS: {
        draft.loggedIn = !!action.loggedIn;
        break;
      }
    }
  },
  initialBookmarkState,
);
